import { memo, useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { IOutletAuth } from "types";
import { useIssueList } from "modules/IssueList/IssueList.hooks";
import { useSolutionList } from "modules/SolutionList/SolutionList.hooks";
import { useActionGroupsList } from "modules/GroupList/GroupList.hooks";
import { APP_ROUTES } from "const";

export type TimeFilter = "week" | "month" | "year" | "all";

// Animated counter hook
function useAnimatedCounter(target: number, duration: number = 2000) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const startTime = Date.now();
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            // Easing function for smooth deceleration
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(animate);
          };
          animate();
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration, hasAnimated]);

  return { count, ref };
}

// Staggered animation wrapper
function AnimatedSection({
  children,
  delay = 0,
  className = ""
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
        transition: "opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
      }}
    >
      {children}
    </div>
  );
}

// Metric card with animated counter
function MetricCard({
  value,
  label,
  sublabel,
  icon,
  accentColor,
  delay = 0,
}: {
  value: number;
  label: string;
  sublabel: string;
  icon: React.ReactNode;
  accentColor: string;
  delay?: number;
}) {
  const { count, ref } = useAnimatedCounter(value);

  return (
    <AnimatedSection delay={delay}>
      <div
        ref={ref}
        className="group relative overflow-hidden"
        style={{
          background: "#FFFEF9",
          borderRadius: "2px",
          border: "1px solid #E7E5E4",
        }}
      >
        {/* Top accent line */}
        <div
          className="absolute top-0 left-0 right-0 h-1"
          style={{ background: accentColor }}
        />

        <div className="p-6 lg:p-8">
          {/* Icon */}
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center mb-6"
            style={{ background: `${accentColor}15`, color: accentColor }}
          >
            {icon}
          </div>

          {/* Counter */}
          <div className="mb-2">
            <span
              className="text-5xl lg:text-6xl font-light tracking-tight"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                color: "#1C1917",
              }}
            >
              {count.toLocaleString()}
            </span>
          </div>

          {/* Label */}
          <p
            className="text-sm font-semibold uppercase tracking-widest mb-1"
            style={{ color: accentColor }}
          >
            {label}
          </p>
          <p
            className="text-sm"
            style={{ color: "#78716C", fontFamily: "'Source Sans 3', sans-serif" }}
          >
            {sublabel}
          </p>
        </div>

        {/* Hover effect */}
        <div
          className="absolute bottom-0 left-0 right-0 h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
          style={{ background: accentColor, transformOrigin: "left" }}
        />
      </div>
    </AnimatedSection>
  );
}

// Category bar visualization
function CategoryBar({
  name,
  count,
  percentage,
  color,
  delay = 0,
  isSelected,
  onClick,
}: {
  name: string;
  count: number;
  percentage: number;
  color: string;
  delay?: number;
  isSelected: boolean;
  onClick: () => void;
}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <button
      ref={ref}
      onClick={onClick}
      className={`w-full text-left group transition-all duration-300 ${
        isSelected ? "scale-[1.02]" : "hover:scale-[1.01]"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span
          className="text-sm font-medium"
          style={{
            color: isSelected ? color : "#1C1917",
            fontFamily: "'Source Sans 3', sans-serif",
          }}
        >
          {name}
        </span>
        <div className="flex items-center gap-2">
          <span
            className="text-xs font-mono"
            style={{ color: "#78716C" }}
          >
            {count}
          </span>
          <span
            className="text-xs font-bold"
            style={{ color }}
          >
            {percentage}%
          </span>
        </div>
      </div>
      <div
        className="h-2 rounded-full overflow-hidden"
        style={{ background: "#F5F5F4" }}
      >
        <div
          className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{
            width: isVisible ? `${percentage}%` : "0%",
            background: color,
            boxShadow: isSelected ? `0 0 12px ${color}40` : "none",
          }}
        />
      </div>
    </button>
  );
}

// Trending item card
function TrendingCard({
  rank,
  title,
  type,
  metric,
  metricLabel,
  href,
  delay = 0,
}: {
  rank: number;
  title: string;
  type: "issue" | "solution" | "group";
  metric: number;
  metricLabel: string;
  href: string;
  delay?: number;
}) {
  const typeConfig = {
    issue: { color: "#C2410C", label: "ISSUE", bg: "rgba(194, 65, 12, 0.08)" },
    solution: { color: "#059669", label: "SOLUTION", bg: "rgba(5, 150, 105, 0.08)" },
    group: { color: "#7C3AED", label: "ACTION GROUP", bg: "rgba(124, 58, 237, 0.08)" },
  };
  const config = typeConfig[type];

  return (
    <AnimatedSection delay={delay}>
      <Link
        to={href}
        className="group block relative overflow-hidden"
        style={{
          background: "#FFFEF9",
          border: "1px solid #E7E5E4",
          borderRadius: "2px",
        }}
      >
        {/* Rank badge */}
        <div
          className="absolute top-0 left-0 w-8 h-8 flex items-center justify-center text-xs font-bold"
          style={{
            background: rank === 1 ? config.color : "#F5F5F4",
            color: rank === 1 ? "#FFFEF9" : "#78716C",
          }}
        >
          {rank}
        </div>

        <div className="p-5 pl-12">
          {/* Type label */}
          <span
            className="text-[10px] font-bold tracking-widest"
            style={{ color: config.color }}
          >
            {config.label}
          </span>

          {/* Title */}
          <h4
            className="text-base font-semibold mt-1 mb-3 line-clamp-2 group-hover:underline decoration-1 underline-offset-2"
            style={{
              color: "#1C1917",
              fontFamily: "'Source Serif 4', Georgia, serif",
            }}
          >
            {title}
          </h4>

          {/* Metric */}
          <div className="flex items-center gap-2">
            <span
              className="text-xl font-light"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                color: config.color,
              }}
            >
              {metric}
            </span>
            <span className="text-xs" style={{ color: "#78716C" }}>
              {metricLabel}
            </span>
          </div>
        </div>

        {/* Hover accent */}
        <div
          className="absolute bottom-0 left-0 right-0 h-0.5 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
          style={{ background: config.color, transformOrigin: "left" }}
        />
      </Link>
    </AnimatedSection>
  );
}

// Country row
function CountryRow({
  country,
  flag,
  issues,
  solutions,
  groups,
  total,
  maxTotal,
  delay = 0,
}: {
  country: string;
  flag: string;
  issues: number;
  solutions: number;
  groups: number;
  total: number;
  maxTotal: number;
  delay?: number;
}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className="group py-4 border-b transition-colors hover:bg-stone-700/30"
      style={{ borderColor: "#44403C" }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <span className="text-xl">{flag}</span>
          <span
            className="text-sm font-medium"
            style={{ color: "#FFFEF9" }}
          >
            {country}
          </span>
        </div>
        <span
          className="text-sm font-mono"
          style={{ color: "#78716C" }}
        >
          {total} total
        </span>
      </div>

      {/* Stacked bar */}
      <div className="flex gap-0.5 h-1.5 rounded-full overflow-hidden" style={{ background: "#F5F5F4" }}>
        <div
          className="h-full transition-all duration-1000 ease-out"
          style={{
            width: isVisible ? `${(issues / maxTotal) * 100}%` : "0%",
            background: "#C2410C",
          }}
        />
        <div
          className="h-full transition-all duration-1000 ease-out"
          style={{
            width: isVisible ? `${(solutions / maxTotal) * 100}%` : "0%",
            background: "#059669",
            transitionDelay: "100ms",
          }}
        />
        <div
          className="h-full transition-all duration-1000 ease-out"
          style={{
            width: isVisible ? `${(groups / maxTotal) * 100}%` : "0%",
            background: "#7C3AED",
            transitionDelay: "200ms",
          }}
        />
      </div>
    </div>
  );
}

// Skill demand card
function SkillCard({
  name,
  needed,
  maxNeeded,
  groupCount,
  delay = 0,
}: {
  name: string;
  needed: number;
  maxNeeded: number;
  groupCount: number;
  delay?: number;
}) {
  return (
    <AnimatedSection delay={delay}>
      <div
        className="p-4 transition-all hover:shadow-md"
        style={{
          background: "#FFFEF9",
          border: "1px solid #E7E5E4",
          borderRadius: "2px",
        }}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4
              className="text-sm font-semibold"
              style={{ color: "#1C1917" }}
            >
              {name}
            </h4>
            <p className="text-xs" style={{ color: "#78716C" }}>
              Needed by {groupCount} group{groupCount !== 1 ? "s" : ""}
            </p>
          </div>
          <span
            className="text-lg font-light"
            style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              color: "#7C3AED",
            }}
          >
            {needed}
          </span>
        </div>
        <div className="h-1 rounded-full overflow-hidden" style={{ background: "#F5F5F4" }}>
          <div
            className="h-full rounded-full"
            style={{
              width: `${(needed / maxNeeded) * 100}%`,
              background: "linear-gradient(90deg, #7C3AED, #A78BFA)",
            }}
          />
        </div>
      </div>
    </AnimatedSection>
  );
}

// Action group card
function ActionGroupCard({
  title,
  members,
  location,
  id,
  delay = 0,
}: {
  title: string;
  members: number;
  location?: string;
  id: string;
  delay?: number;
}) {
  return (
    <AnimatedSection delay={delay}>
      <Link
        to={`${APP_ROUTES.ACTION_GROUP}/${id}`}
        className="group block p-5 transition-all hover:shadow-lg"
        style={{
          background: "#FFFEF9",
          border: "1px solid #E7E5E4",
          borderRadius: "2px",
        }}
      >
        <div className="flex items-start justify-between mb-3">
          <h4
            className="text-base font-semibold line-clamp-2 group-hover:text-purple-700 transition-colors"
            style={{
              color: "#1C1917",
              fontFamily: "'Source Serif 4', Georgia, serif",
            }}
          >
            {title}
          </h4>
          <svg
            className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2"
            style={{ color: "#7C3AED" }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </div>

        <div className="flex items-center gap-4 text-xs" style={{ color: "#78716C" }}>
          <div className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="font-semibold" style={{ color: "#7C3AED" }}>{members}</span>
            <span>members</span>
          </div>
          {location && (
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              <span className="truncate max-w-[120px]">{location}</span>
            </div>
          )}
        </div>
      </Link>
    </AnimatedSection>
  );
}

function Impact() {
  const authContext = useOutletContext<IOutletAuth>();
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("all");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Fetch all data
  const { data: issuesData, isLoading: issuesLoading } = useIssueList(
    { user: "", skills: [], tags: [], category: [], zoom: 1, lat: "", lng: "" },
    1045,
    100
  );

  const { data: solutionsData, isLoading: solutionsLoading } = useSolutionList(
    { user: "", skills: [], category: [], tags: [], zoom: 1, lat: "", lng: "" },
    1045,
    100
  );

  const { data: actionGroupsData, isLoading: groupsLoading } = useActionGroupsList(
    { user: "", skills: [], category: [], tags: [], zoom: 1, lat: "", lng: "" },
    1045
  );

  const issues = useMemo(() =>
    issuesData?.pages?.flatMap(page => page.data.items) || [],
    [issuesData]
  );

  const solutions = useMemo(() =>
    solutionsData?.pages?.flatMap(page => page.data.items) || [],
    [solutionsData]
  );

  const actionGroups = useMemo(() =>
    actionGroupsData?.pages?.flatMap(page => page.data.items) || [],
    [actionGroupsData]
  );

  // Filter data by time
  const filterByTime = useCallback((items: any[], dateField: string = "createdAt") => {
    if (timeFilter === "all") return items;
    const now = new Date();
    const cutoff = new Date();
    switch (timeFilter) {
      case "week": cutoff.setDate(now.getDate() - 7); break;
      case "month": cutoff.setMonth(now.getMonth() - 1); break;
      case "year": cutoff.setFullYear(now.getFullYear() - 1); break;
    }
    return items.filter(item => new Date(item[dateField]) >= cutoff);
  }, [timeFilter]);

  const filteredIssues = useMemo(() => filterByTime(issues), [issues, filterByTime]);
  const filteredSolutions = useMemo(() => filterByTime(solutions), [solutions, filterByTime]);
  const filteredActionGroups = useMemo(() => filterByTime(actionGroups), [actionGroups, filterByTime]);

  // Category breakdown
  const categoryData = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredIssues.forEach((issue) => {
      const category = issue.primaryClassification?.title || issue.secondaryClassification?.title || "Other";
      counts[category] = (counts[category] || 0) + 1;
    });
    const total = filteredIssues.length || 1;
    const colors = ["#C2410C", "#059669", "#7C3AED", "#0891B2", "#DB2777", "#EA580C", "#2563EB", "#DC2626"];
    return Object.entries(counts)
      .map(([name, count], i) => ({
        name,
        count,
        percentage: Math.round((count / total) * 100),
        color: colors[i % colors.length],
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [filteredIssues]);

  // Geographic data
  const countryData = useMemo(() => {
    const data: Record<string, { issues: number; solutions: number; groups: number }> = {};
    filteredIssues.forEach((issue) => {
      const country = issue.location?.country;
      if (country) {
        if (!data[country]) data[country] = { issues: 0, solutions: 0, groups: 0 };
        data[country].issues++;
      }
    });
    filteredSolutions.forEach((solution) => {
      const country = solution.issue?.location?.country;
      if (country) {
        if (!data[country]) data[country] = { issues: 0, solutions: 0, groups: 0 };
        data[country].solutions++;
      }
    });
    filteredActionGroups.forEach((group) => {
      const country = group.location?.country;
      if (country) {
        if (!data[country]) data[country] = { issues: 0, solutions: 0, groups: 0 };
        data[country].groups++;
      }
    });

    const countryFlags: Record<string, string> = {
      Nigeria: "🇳🇬", Kenya: "🇰🇪", "United States": "🇺🇸", India: "🇮🇳", Indonesia: "🇮🇩",
      Brazil: "🇧🇷", Philippines: "🇵🇭", Bangladesh: "🇧🇩", "South Africa": "🇿🇦", Uganda: "🇺🇬",
      Ghana: "🇬🇭", Ethiopia: "🇪🇹", Tanzania: "🇹🇿", Myanmar: "🇲🇲", Mozambique: "🇲🇿",
      Mexico: "🇲🇽", Peru: "🇵🇪", Colombia: "🇨🇴", Pakistan: "🇵🇰", Afghanistan: "🇦🇫",
      Chad: "🇹🇩", Mali: "🇲🇱", Sudan: "🇸🇩", "South Sudan": "🇸🇸",
    };

    return Object.entries(data)
      .map(([country, counts]) => ({
        country,
        flag: countryFlags[country] || "🌍",
        ...counts,
        total: counts.issues + counts.solutions + counts.groups,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 6);
  }, [filteredIssues, filteredSolutions, filteredActionGroups]);

  const maxCountryTotal = countryData[0]?.total || 1;

  // Skills data
  const skillsData = useMemo(() => {
    const skillCounts: Record<string, { needed: number; groups: Set<string> }> = {};
    filteredActionGroups.forEach((group) => {
      (group.skills || []).forEach((skill: any) => {
        const name = skill.skill?.title || skill.title || "Unknown";
        if (!skillCounts[name]) skillCounts[name] = { needed: 0, groups: new Set() };
        skillCounts[name].needed++;
        skillCounts[name].groups.add(group.title);
      });
    });
    return Object.entries(skillCounts)
      .map(([name, data]) => ({ name, needed: data.needed, groupCount: data.groups.size }))
      .sort((a, b) => b.needed - a.needed)
      .slice(0, 6);
  }, [filteredActionGroups]);

  const maxSkillNeeded = skillsData[0]?.needed || 1;

  // Trending items
  const trendingItems = useMemo(() => {
    const topIssues = [...filteredIssues]
      .sort((a, b) => (b.numberOfRatings || 0) - (a.numberOfRatings || 0))
      .slice(0, 2)
      .map((issue, i) => ({
        rank: i + 1,
        title: issue.title,
        type: "issue" as const,
        metric: issue.numberOfRatings || 0,
        metricLabel: "ratings",
        href: `${APP_ROUTES.ISSUE}/${issue.id}`,
      }));

    const topSolutions = [...filteredSolutions]
      .sort((a, b) => (b.numberOfRatings || 0) - (a.numberOfRatings || 0))
      .slice(0, 2)
      .map((solution, i) => ({
        rank: i + 1,
        title: solution.title,
        type: "solution" as const,
        metric: solution.numberOfRatings || 0,
        metricLabel: "ratings",
        href: `${APP_ROUTES.SOLUTION}/${solution.id}`,
      }));

    const topGroups = [...filteredActionGroups]
      .sort((a, b) => (b.numberOfMembers || 0) - (a.numberOfMembers || 0))
      .slice(0, 2)
      .map((group, i) => ({
        rank: i + 1,
        title: group.title,
        type: "group" as const,
        metric: group.numberOfMembers || 0,
        metricLabel: "members",
        href: `${APP_ROUTES.ACTION_GROUP}/${group.id}`,
      }));

    return [...topIssues, ...topSolutions, ...topGroups];
  }, [filteredIssues, filteredSolutions, filteredActionGroups]);

  // Top action groups
  const topActionGroups = useMemo(() =>
    [...filteredActionGroups]
      .sort((a, b) => (b.numberOfMembers || 0) - (a.numberOfMembers || 0))
      .slice(0, 4),
    [filteredActionGroups]
  );

  const totalMembers = useMemo(() =>
    filteredActionGroups.reduce((sum, g) => sum + (g.numberOfMembers || 0), 0),
    [filteredActionGroups]
  );

  const isLoading = issuesLoading || solutionsLoading || groupsLoading;

  const timeOptions: { value: TimeFilter; label: string }[] = [
    { value: "week", label: "This Week" },
    { value: "month", label: "This Month" },
    { value: "year", label: "This Year" },
    { value: "all", label: "All Time" },
  ];

  return (
    <div
      className="min-h-screen"
      style={{
        background: "#FAFAF9",
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        backgroundBlendMode: "soft-light",
      }}
    >
      {/* Hero Section */}
      <section
        className="relative overflow-hidden"
        style={{
          background: "linear-gradient(165deg, #1C1917 0%, #292524 60%, #3D3633 100%)",
        }}
      >
        {/* Topographic pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 800'%3E%3Cg fill='none' stroke='%23ffffff' stroke-width='1'%3E%3Ccircle cx='400' cy='400' r='50'/%3E%3Ccircle cx='400' cy='400' r='100'/%3E%3Ccircle cx='400' cy='400' r='150'/%3E%3Ccircle cx='400' cy='400' r='200'/%3E%3Ccircle cx='400' cy='400' r='250'/%3E%3Ccircle cx='400' cy='400' r='300'/%3E%3Ccircle cx='400' cy='400' r='350'/%3E%3Ccircle cx='400' cy='400' r='400'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: "100% 100%",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
          {/* Eyebrow */}
          <AnimatedSection>
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span
                  className="text-xs font-medium uppercase tracking-widest"
                  style={{ color: "#A8A29E" }}
                >
                  Live Platform Data
                </span>
              </div>
              <div className="h-px flex-1 max-w-[100px]" style={{ background: "#44403C" }} />
            </div>
          </AnimatedSection>

          {/* Main headline */}
          <AnimatedSection delay={100}>
            <h1
              className="text-4xl sm:text-5xl lg:text-7xl font-light leading-[0.95] mb-6"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                color: "#FFFEF9",
              }}
            >
              The Power of
              <br />
              <span
                className="font-semibold"
                style={{
                  background: "linear-gradient(135deg, #FB923C 0%, #C2410C 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Collective Action
              </span>
            </h1>
          </AnimatedSection>

          {/* Subheadline */}
          <AnimatedSection delay={200}>
            <p
              className="text-lg lg:text-xl max-w-2xl mb-10"
              style={{
                color: "#A8A29E",
                fontFamily: "'Source Sans 3', sans-serif",
                lineHeight: 1.6,
              }}
            >
              Real-time insights into how communities worldwide are identifying
              challenges, proposing solutions, and organizing for change.
            </p>
          </AnimatedSection>

          {/* Time filter */}
          <AnimatedSection delay={300}>
            <div className="flex flex-wrap items-center gap-2">
              {timeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTimeFilter(option.value)}
                  className="px-4 py-2 text-sm font-medium transition-all duration-300"
                  style={{
                    background: timeFilter === option.value ? "#C2410C" : "transparent",
                    color: timeFilter === option.value ? "#FFFEF9" : "#78716C",
                    border: `1px solid ${timeFilter === option.value ? "#C2410C" : "#44403C"}`,
                    borderRadius: "2px",
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </AnimatedSection>
        </div>

        {/* Bottom accent */}
        <div
          className="absolute bottom-0 left-0 right-0 h-1"
          style={{
            background: "linear-gradient(90deg, #C2410C, #FB923C, #C2410C)",
          }}
        />
      </section>

      {isLoading ? (
        <div className="flex items-center justify-center py-32">
          <div className="relative">
            <div className="w-16 h-16 border-2 border-stone-200 rounded-full" />
            <div
              className="absolute inset-0 w-16 h-16 border-2 border-transparent border-t-orange-600 rounded-full animate-spin"
            />
          </div>
        </div>
      ) : (
        <>
          {/* Key Metrics Section */}
          <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                value={filteredIssues.length}
                label="Issues Identified"
                sublabel="Community-reported challenges"
                delay={0}
                accentColor="#C2410C"
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                }
              />
              <MetricCard
                value={filteredSolutions.length}
                label="Solutions Proposed"
                sublabel="Ideas to address challenges"
                delay={100}
                accentColor="#059669"
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                }
              />
              <MetricCard
                value={filteredActionGroups.length}
                label="Action Groups"
                sublabel="Teams organized for impact"
                delay={200}
                accentColor="#7C3AED"
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                }
              />
              <MetricCard
                value={totalMembers}
                label="Active Members"
                sublabel="People taking action"
                delay={300}
                accentColor="#0891B2"
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m3 5.197v-1a4 4 0 00-2-3.464" />
                  </svg>
                }
              />
            </div>
          </section>

          {/* Trending + Categories Section */}
          <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-16">
            <div className="grid lg:grid-cols-5 gap-8">
              {/* Trending - 3 columns */}
              <div className="lg:col-span-3">
                <AnimatedSection>
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="w-8 h-8 flex items-center justify-center"
                      style={{ background: "#FEE2E2", borderRadius: "2px" }}
                    >
                      <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                      </svg>
                    </div>
                    <h2
                      className="text-xl font-semibold"
                      style={{
                        fontFamily: "'Source Serif 4', Georgia, serif",
                        color: "#1C1917",
                      }}
                    >
                      Trending Now
                    </h2>
                  </div>
                </AnimatedSection>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {trendingItems.slice(0, 6).map((item, i) => (
                    <TrendingCard key={`${item.type}-${i}`} {...item} delay={i * 50} />
                  ))}
                </div>
              </div>

              {/* Categories - 2 columns */}
              <div className="lg:col-span-2">
                <AnimatedSection>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 flex items-center justify-center"
                        style={{ background: "#DBEAFE", borderRadius: "2px" }}
                      >
                        <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
                        </svg>
                      </div>
                      <h2
                        className="text-xl font-semibold"
                        style={{
                          fontFamily: "'Source Serif 4', Georgia, serif",
                          color: "#1C1917",
                        }}
                      >
                        By Category
                      </h2>
                    </div>
                    {selectedCategory && (
                      <button
                        onClick={() => setSelectedCategory(null)}
                        className="text-xs font-medium px-2 py-1 rounded hover:bg-stone-100 transition-colors"
                        style={{ color: "#C2410C" }}
                      >
                        Clear filter
                      </button>
                    )}
                  </div>
                </AnimatedSection>

                <div
                  className="p-6"
                  style={{
                    background: "#FFFEF9",
                    border: "1px solid #E7E5E4",
                    borderRadius: "2px",
                  }}
                >
                  <div className="space-y-5">
                    {categoryData.map((cat, i) => (
                      <CategoryBar
                        key={cat.name}
                        {...cat}
                        delay={i * 80}
                        isSelected={selectedCategory === cat.name}
                        onClick={() => setSelectedCategory(selectedCategory === cat.name ? null : cat.name)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Geographic + Skills Section */}
          <section
            className="py-16"
            style={{ background: "#1C1917" }}
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="grid lg:grid-cols-2 gap-12">
                {/* Geographic Reach */}
                <div>
                  <AnimatedSection>
                    <div className="flex items-center gap-3 mb-8">
                      <h2
                        className="text-2xl font-light"
                        style={{
                          fontFamily: "'Source Serif 4', Georgia, serif",
                          color: "#FFFEF9",
                        }}
                      >
                        Geographic Reach
                      </h2>
                      <div className="h-px flex-1" style={{ background: "#44403C" }} />
                    </div>
                  </AnimatedSection>

                  <div
                    className="p-6"
                    style={{
                      background: "#292524",
                      borderRadius: "2px",
                    }}
                  >
                    {/* Legend */}
                    <div className="flex items-center gap-6 mb-6 text-xs" style={{ color: "#A8A29E" }}>
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-2 rounded-sm" style={{ background: "#C2410C" }} />
                        Issues
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-2 rounded-sm" style={{ background: "#059669" }} />
                        Solutions
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-2 rounded-sm" style={{ background: "#7C3AED" }} />
                        Groups
                      </div>
                    </div>

                    {countryData.length > 0 ? (
                      countryData.map((country, i) => (
                        <CountryRow
                          key={country.country}
                          {...country}
                          maxTotal={maxCountryTotal}
                          delay={i * 100}
                        />
                      ))
                    ) : (
                      <p className="text-center py-8" style={{ color: "#78716C" }}>
                        No geographic data available
                      </p>
                    )}

                    <Link
                      to={APP_ROUTES.ISSUES}
                      className="flex items-center justify-center gap-2 mt-6 py-3 text-sm font-medium transition-all hover:bg-stone-700/50"
                      style={{
                        color: "#FB923C",
                        border: "1px solid #44403C",
                        borderRadius: "2px",
                      }}
                    >
                      Explore on Map
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </Link>
                  </div>
                </div>

                {/* Skills in Demand */}
                <div>
                  <AnimatedSection>
                    <div className="flex items-center gap-3 mb-8">
                      <h2
                        className="text-2xl font-light"
                        style={{
                          fontFamily: "'Source Serif 4', Georgia, serif",
                          color: "#FFFEF9",
                        }}
                      >
                        Skills in Demand
                      </h2>
                      <span
                        className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded"
                        style={{ background: "#7C3AED", color: "#FFFEF9" }}
                      >
                        Volunteer
                      </span>
                      <div className="h-px flex-1" style={{ background: "#44403C" }} />
                    </div>
                  </AnimatedSection>

                  <div className="grid gap-3">
                    {skillsData.length > 0 ? (
                      skillsData.map((skill, i) => (
                        <SkillCard
                          key={skill.name}
                          {...skill}
                          maxNeeded={maxSkillNeeded}
                          delay={i * 80}
                        />
                      ))
                    ) : (
                      <div
                        className="p-8 text-center"
                        style={{
                          background: "#292524",
                          borderRadius: "2px",
                          color: "#78716C",
                        }}
                      >
                        No skills data available
                      </div>
                    )}
                  </div>

                  <AnimatedSection delay={500}>
                    <Link
                      to={APP_ROUTES.ACTION_GROUP_LIST}
                      className="flex items-center justify-center gap-2 mt-6 py-3 text-sm font-medium transition-all"
                      style={{
                        background: "#7C3AED",
                        color: "#FFFEF9",
                        borderRadius: "2px",
                      }}
                    >
                      Find Groups That Need You
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </Link>
                  </AnimatedSection>
                </div>
              </div>
            </div>
          </section>

          {/* Action Groups Section */}
          <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
            <AnimatedSection>
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 flex items-center justify-center"
                    style={{ background: "#EDE9FE", borderRadius: "2px" }}
                  >
                    <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h2
                    className="text-2xl font-semibold"
                    style={{
                      fontFamily: "'Source Serif 4', Georgia, serif",
                      color: "#1C1917",
                    }}
                  >
                    Active Action Groups
                  </h2>
                </div>
                <Link
                  to={APP_ROUTES.ACTION_GROUP_LIST}
                  className="text-sm font-medium hover:underline"
                  style={{ color: "#7C3AED" }}
                >
                  View all groups →
                </Link>
              </div>
            </AnimatedSection>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {topActionGroups.map((group, i) => (
                <ActionGroupCard
                  key={group.id}
                  title={group.title}
                  members={group.numberOfMembers || 0}
                  location={group.location?.country}
                  id={group.id}
                  delay={i * 80}
                />
              ))}
            </div>
          </section>

          {/* CTA Section */}
          {!authContext?.isAuth && (
            <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-16">
              <AnimatedSection>
                <div
                  className="relative overflow-hidden p-10 lg:p-16"
                  style={{
                    background: "linear-gradient(135deg, #C2410C 0%, #EA580C 50%, #F97316 100%)",
                    borderRadius: "2px",
                  }}
                >
                  {/* Pattern overlay */}
                  <div
                    className="absolute inset-0 opacity-10"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 20.5V18H0v-2h20v-2.5L25 17l-5 3.5z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                    }}
                  />

                  <div className="relative max-w-2xl">
                    <h3
                      className="text-3xl lg:text-4xl font-light mb-4"
                      style={{
                        fontFamily: "'Source Serif 4', Georgia, serif",
                        color: "#FFFEF9",
                      }}
                    >
                      Ready to Join the
                      <br />
                      <span className="font-semibold">Movement?</span>
                    </h3>
                    <p
                      className="text-lg mb-8"
                      style={{ color: "rgba(255, 254, 249, 0.8)" }}
                    >
                      Be part of a global community identifying challenges, creating solutions,
                      and taking action together.
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <Link
                        to={APP_ROUTES.SIGN_UP}
                        className="inline-flex items-center px-6 py-3 font-semibold transition-all hover:shadow-lg"
                        style={{
                          background: "#FFFEF9",
                          color: "#C2410C",
                          borderRadius: "2px",
                        }}
                      >
                        Get Started Free
                        <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </Link>
                      <Link
                        to={APP_ROUTES.ISSUES}
                        className="inline-flex items-center px-6 py-3 font-semibold transition-all"
                        style={{
                          background: "transparent",
                          color: "#FFFEF9",
                          border: "1px solid rgba(255, 254, 249, 0.3)",
                          borderRadius: "2px",
                        }}
                      >
                        Explore Issues
                      </Link>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default memo(Impact);
